import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import NewRegisterBtn from '../../../../components/Buttons/NewRegisterBtn';
import { backendErrorNotification } from '../../../../components/Notification';
import {
  ActionButtonsContainer,
  DeleteIconTable,
  EditIconTable,
} from '../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../enums/httpStatus';
import { accessBlocksApi } from '../../../../services/requests';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import { AccessBlock } from '../../../../types/apiResponse/accessBlock';
import { ApiResPaginated } from '../../../../types/apiResponseTypes';
import checkPermission from '../../../../utils/checkPermission';
import { weekdays } from './constants';
import AccessBlockFormModal from './Components/AccessBlockFormModal';
import DeleteAccessBlockModal from './Components/DeleteAccessBlockModal';
import * as s from './styles';

const AccessBlocksPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [data, setData] = useState<AccessBlock[]>([]);
  const [selectedAccessBlock, setSelectedAccessBlock] = useState<AccessBlock | null>(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const canCreate = checkPermission('accessBlock-create');
  const canEdit = checkPermission('accessBlock-edit');
  const canDelete = checkPermission('accessBlock-delete');

  const closeModals = () => {
    setShowFormModal(false);
    setShowDeleteModal(false);
    setSelectedAccessBlock(null);
  };

  const getScheduleLabel = (row: AccessBlock): string => {
    if (row.scheduleType === 'weekday') {
      return row.weekdays
        .map((day) => weekdays().find((weekday) => weekday.value === day)?.label)
        .filter(Boolean)
        .join(', ');
    }

    if (row.scheduleType === 'specific_date') {
      return row.specificDate ?? '-';
    }

    return `${row.startsOn ?? '-'} → ${row.endsOn ?? '-'}`;
  };

  const getTimeLabel = (row: AccessBlock): string => (
    row.allDay ? t('allDay') as string : `${row.startTime ?? '-'} → ${row.endTime ?? '-'}`
  );

  const getActionLabel = (action: AccessBlock['actions'][number]): string => (
    action === 'login' ? t('login') as string : t('makeOrder') as string
  );

  const actionsButtons = (row: AccessBlock) => (
    <ActionButtonsContainer>
      {canEdit && <EditIconTable onClick={() => {
        setSelectedAccessBlock(row);
        setShowFormModal(true);
      }} />}
      {canDelete && <DeleteIconTable onClick={() => {
        setSelectedAccessBlock(row);
        setShowDeleteModal(true);
      }} />}
    </ActionButtonsContainer>
  );

  const columns: Column<AccessBlock>[] = [
    { label: t('id') as string, accessor: 'id' },
    { label: t('description') as string, accessor: 'description' },
    {
      label: t('accessBlockType') as string,
      Cell: (row) => (
        <s.RuleType $isBlock={row.type === 'block'}>
          {t(row.type === 'block' ? 'block' : 'release') as string}
        </s.RuleType>
      ),
    },
    {
      label: t('audience') as string,
      Cell: (row) => row.audience?.description ?? '-',
    },
    {
      label: t('accessActions') as string,
      Cell: (row) => row.actions.map(getActionLabel).join(', '),
    },
    {
      label: t('scheduleType') as string,
      Cell: getScheduleLabel,
    },
    {
      label: t('timeRange') as string,
      Cell: getTimeLabel,
    },
    {
      label: t('actions') as string,
      Cell: actionsButtons,
    },
  ];

  const requestPage: RequestPage = async ({ search, page, quantityPerPage }) => {
    const source = accessBlocksApi.axios.CancelToken.source();

    try {
      accessBlocksApi.query = `?search=${encodeURIComponent(search)}&page=${page}&quantityPerPage=${quantityPerPage}`;
      const response = await accessBlocksApi.index(source.token);
      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      const result: ApiResPaginated<AccessBlock[]> = response.data;
      return {
        data: result.data,
        hasMore: result.data.length > 0,
        totalPage: result.paginated.totalPage,
      };
    } catch (err) {
      if (!accessBlocksApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }

      return { data: [], hasMore: false, totalPage: 0 };
    }
  };

  return (
    <ContentContainer title={t('accessBlocks') as string}>
      {canCreate && (
        <NewRegisterBtn
          onClick={() => {
            setSelectedAccessBlock(null);
            setShowFormModal(true);
          }}
          style={{ marginBottom: '1rem', marginLeft: '0' }}
        />
      )}

      <AsyncTable
        tableName={t('accessBlocks') as string}
        columns={columns}
        value={data}
        onChange={setData}
        requestPage={requestPage}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage') as string,
          searchPlaceholder: t('search') as string,
        }}
      />

      {showFormModal && (
        <AccessBlockFormModal
          showModal={showFormModal}
          selectedAccessBlock={selectedAccessBlock}
          closeModal={closeModals}
          callback={() => setUpdateTable((value) => value + 1)}
        />
      )}

      {showDeleteModal && selectedAccessBlock && (
        <DeleteAccessBlockModal
          showModal={showDeleteModal}
          selectedAccessBlock={selectedAccessBlock}
          closeModal={closeModals}
          callback={() => setUpdateTable((value) => value + 1)}
        />
      )}
    </ContentContainer>
  );
};

export default AccessBlocksPage;
